/**
 * SharePoint Job Form
 * Create and edit SharePoint image metadata extraction jobs
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Search, Trash2, Save, CheckCircle2, XCircle, AlertCircle, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchSharePointJob,
  createSharePointJob,
  updateSharePointJob,
  validateAndProvisionColumns,
  type SharePointJob,
  type SharePointSiteConfig,
  type SharePointNotificationConfig,
  type EmailRecipient,
  type ColumnValidationResult,
} from "@/lib/sharepoint-api";
import { SitePickerDialog } from "@/components/sharepoint/site-picker-dialog";
import {
  buildCronExpression,
  formatTime,
  formatTimeBasedSchedule,
  parseCronExpression,
  type DayOfWeek,
  type FrequencyType,
  type HourlyInterval,
} from "@/lib/cronBuilder";
import { SharePointLayout } from "./layout";

interface SharePointJobFormProps {
  jobId?: string;
  mode: "create" | "edit";
}

type ScheduleUiMode = "simple" | "advanced";

function parseSharePointScheduleExpression(expression: string) {
  const parts = expression.trim().split(/\s+/);
  // Support legacy 6-part cron by dropping the leading seconds field when present.
  if (parts.length === 6) {
    return parseCronExpression(parts.slice(1).join(" "));
  }
  return parseCronExpression(expression);
}

export default function SharePointJobForm({ jobId, mode }: SharePointJobFormProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [schedule, setSchedule] = useState("");
  const [scheduleDescription, setScheduleDescription] = useState("");
  const [scheduleDescriptionTouched, setScheduleDescriptionTouched] = useState(false);
  const [sites, setSites] = useState<SharePointSiteConfig[]>([]);

  // Schedule UI state (stored as a cron string in job config, but edited via friendly UI)
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleUiMode, setScheduleUiMode] = useState<ScheduleUiMode>("simple");
  const [timeHour, setTimeHour] = useState(21);
  const [timeMinute, setTimeMinute] = useState(0);
  const [frequency, setFrequency] = useState<FrequencyType>("daily");
  const [specificDays, setSpecificDays] = useState<DayOfWeek[]>([]);
  const [hourlyInterval, setHourlyInterval] = useState<HourlyInterval | undefined>(undefined);

  // Site picker UI state
  const [sitePickerOpen, setSitePickerOpen] = useState(false);
  const [sitePickerIndex, setSitePickerIndex] = useState<number | null>(null);
  const [sitePickerInitialUrl, setSitePickerInitialUrl] = useState<string>("");

  // Column validation state per site (indexed by site index)
  const [columnValidationStatus, setColumnValidationStatus] = useState<
    Record<number, {
      status: 'pending' | 'validating' | 'valid' | 'error';
      result?: ColumnValidationResult;
      error?: string;
    }>
  >({});

  useEffect(() => {
    if (mode === "edit" && jobId) {
      loadJob();
    } else {
      // Initialize with one empty site
      setSites([createEmptySite()]);
      setScheduleEnabled(false);
      setScheduleUiMode("simple");
    }
  }, [mode, jobId]);

  const loadJob = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const job = await fetchSharePointJob(jobId);
      setName(job.name);
      setDescription(job.description || "");
      setIsEnabled(job.isEnabled);
      setDryRun(!!job.dryRun);
      const loadedSchedule = job.schedule || "";
      setSchedule(loadedSchedule);
      setScheduleDescription(job.scheduleDescription || "");
      setScheduleDescriptionTouched(false);
      setScheduleEnabled(!!loadedSchedule);

      if (loadedSchedule) {
        const parsed = parseSharePointScheduleExpression(loadedSchedule);
        if (parsed) {
          setScheduleUiMode("simple");
          setTimeHour(parsed.time.hour);
          setTimeMinute(parsed.time.minute);
          setFrequency(parsed.frequency);
          setSpecificDays(parsed.specificDays || []);
          setHourlyInterval(parsed.hourlyInterval);

          // Normalize stored value to 5-part cron in the UI if we can represent it.
          const normalized = buildCronExpression(parsed);
          setSchedule(normalized);
          if (!job.scheduleDescription) {
            setScheduleDescription(formatTimeBasedSchedule(parsed));
          }
        } else {
          setScheduleUiMode("advanced");
        }
      }

      setSites(job.sites.length > 0 ? job.sites : [createEmptySite()]);
    } catch (error: any) {
      toast({
        title: "Error loading job",
        description: error.message,
        variant: "destructive",
      });
      setLocation("/sharepoint/jobs");
    } finally {
      setLoading(false);
    }
  };

  // Keep schedule + description in sync when using the simple editor.
  useEffect(() => {
    if (!scheduleEnabled) {
      if (schedule !== "") setSchedule("");
      if (scheduleDescription !== "") setScheduleDescription("");
      if (scheduleDescriptionTouched) setScheduleDescriptionTouched(false);
      return;
    }

    if (scheduleUiMode !== "simple") return;

    try {
      const timeBased = {
        time: { hour: timeHour, minute: timeMinute },
        frequency,
        specificDays: frequency === "specific-days" ? specificDays : undefined,
        hourlyInterval,
      } as const;

      const nextSchedule = buildCronExpression(timeBased);
      if (schedule !== nextSchedule) setSchedule(nextSchedule);

      if (!scheduleDescriptionTouched) {
        const nextDescription = formatTimeBasedSchedule(timeBased);
        if (scheduleDescription !== nextDescription) setScheduleDescription(nextDescription);
      }
    } catch {
      // Ignore until the user completes required fields (e.g. specific days).
    }
  }, [
    scheduleEnabled,
    scheduleUiMode,
    timeHour,
    timeMinute,
    frequency,
    specificDays,
    hourlyInterval,
    schedule,
    scheduleDescription,
    scheduleDescriptionTouched,
  ]);

  const createEmptySite = (): SharePointSiteConfig => ({
    siteId: "",
    driveId: "",
    columns: {
      width: "ImageWidth",
      height: "ImageHeight",
      dpiX: "DPI_X",
      dpiY: "DPI_Y",
      fileSize: "FileSize_KB",
      colorMode: "ColorMode",
      processedDate: "ProcessedDate",
      processingStatus: "ProcessingStatus",
    },
    maxItemsPerRun: 100,
    folderPath: "",
    folderScanConfig: {
      recursive: true,
      maxDepth: 10,
    },
    qualityConfig: {
      minDPI: 220,
      preferredDPI: 300,
      expectedColorMode: "RGB",
      minWidth: 800,
      minHeight: 600,
    },
  });

  const addSite = () => {
    setSites([...sites, createEmptySite()]);
  };

  const removeSite = (index: number) => {
    setSites(sites.filter((_, i) => i !== index));
  };

  const updateSite = (index: number, updates: Partial<SharePointSiteConfig>) => {
    setSites(sites.map((site, i) => (i === index ? { ...site, ...updates } : site)));
    // Invalidate column validation if siteId or driveId changes
    if ('siteId' in updates || 'driveId' in updates) {
      invalidateColumnValidation(index);
    }
  };

  const openSitePicker = (index: number) => {
    setSitePickerIndex(index);
    setSitePickerInitialUrl("");
    setSitePickerOpen(true);
  };

  const updateSiteColumns = (index: number, columnName: string, value: string) => {
    setSites(
      sites.map((site, i) =>
        i === index
          ? {
              ...site,
              columns: {
                ...site.columns,
                [columnName]: value,
              },
            }
          : site
      )
    );
    // Invalidate column validation when column names change
    invalidateColumnValidation(index);
  };

  const updateFolderScanConfig = (
    index: number,
    field: keyof NonNullable<SharePointSiteConfig["folderScanConfig"]>,
    value: any
  ) => {
    setSites(
      sites.map((site, i) =>
        i === index
          ? {
              ...site,
              folderScanConfig: {
                ...site.folderScanConfig,
                [field]: value,
              },
            }
          : site
      )
    );
  };

  const updateQualityConfig = (
    index: number,
    field: keyof NonNullable<SharePointSiteConfig["qualityConfig"]>,
    value: any
  ) => {
    setSites(
      sites.map((site, i) =>
        i === index
          ? {
              ...site,
              qualityConfig: {
                // Provide defaults for required fields in case qualityConfig is undefined
                minDPI: 220,
                preferredDPI: 300,
                ...site.qualityConfig,
                [field]: value,
              },
            }
          : site
      )
    );
  };

  const updateNotificationConfig = (
    index: number,
    field: keyof SharePointNotificationConfig,
    value: any
  ) => {
    setSites(
      sites.map((site, i) => {
        if (i !== index) return site;
        const newNotifications: SharePointNotificationConfig = {
          enabled: site.notifications?.enabled ?? false,
          fromAddress: site.notifications?.fromAddress ?? "",
          recipients: site.notifications?.recipients ?? [],
          ccRecipients: site.notifications?.ccRecipients,
          thresholds: site.notifications?.thresholds,
          includeDetailedReport: site.notifications?.includeDetailedReport,
          [field]: value,
        };
        return { ...site, notifications: newNotifications };
      })
    );
  };

  const updateNotificationThreshold = (
    index: number,
    field: keyof NonNullable<SharePointNotificationConfig["thresholds"]>,
    value: any
  ) => {
    setSites(
      sites.map((site, i) => {
        if (i !== index) return site;
        const newNotifications: SharePointNotificationConfig = {
          enabled: site.notifications?.enabled ?? false,
          fromAddress: site.notifications?.fromAddress ?? "",
          recipients: site.notifications?.recipients ?? [],
          ccRecipients: site.notifications?.ccRecipients,
          thresholds: {
            ...site.notifications?.thresholds,
            [field]: value,
          },
          includeDetailedReport: site.notifications?.includeDetailedReport,
        };
        return { ...site, notifications: newNotifications };
      })
    );
  };

  const addRecipient = (index: number) => {
    const currentRecipients = sites[index].notifications?.recipients || [];
    updateNotificationConfig(index, "recipients", [
      ...currentRecipients,
      { email: "", name: "" },
    ]);
  };

  const updateRecipient = (
    siteIndex: number,
    recipientIndex: number,
    field: keyof EmailRecipient,
    value: string
  ) => {
    const currentRecipients = [...(sites[siteIndex].notifications?.recipients || [])];
    currentRecipients[recipientIndex] = {
      ...currentRecipients[recipientIndex],
      [field]: value,
    };
    updateNotificationConfig(siteIndex, "recipients", currentRecipients);
  };

  const removeRecipient = (siteIndex: number, recipientIndex: number) => {
    const currentRecipients = [...(sites[siteIndex].notifications?.recipients || [])];
    currentRecipients.splice(recipientIndex, 1);
    updateNotificationConfig(siteIndex, "recipients", currentRecipients);
  };

  // Invalidate column validation when site config changes
  const invalidateColumnValidation = (index: number) => {
    setColumnValidationStatus((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  // Validate and provision columns for a site
  const handleValidateColumns = async (index: number) => {
    const site = sites[index];

    if (!site.siteId || !site.driveId) {
      toast({
        title: "Missing site configuration",
        description: "Please select a SharePoint site and document library first",
        variant: "destructive",
      });
      return;
    }

    setColumnValidationStatus((prev) => ({
      ...prev,
      [index]: { status: 'validating' },
    }));

    try {
      const result = await validateAndProvisionColumns(
        site.siteId,
        site.driveId,
        site.columns
      );

      setColumnValidationStatus((prev) => ({
        ...prev,
        [index]: {
          status: result.success ? 'valid' : 'error',
          result,
          error: result.success ? undefined : result.message,
        },
      }));

      if (result.success) {
        toast({
          title: "Columns validated",
          description: result.createdColumns.length > 0
            ? `Created ${result.createdColumns.length} new columns. All ${result.existingColumns.length + result.createdColumns.length} columns are ready.`
            : `All ${result.existingColumns.length} columns already exist.`,
        });
      } else {
        toast({
          title: "Column validation failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      setColumnValidationStatus((prev) => ({
        ...prev,
        [index]: {
          status: 'error',
          error: error.message,
        },
      }));

      toast({
        title: "Column validation failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Check if all sites have valid column configuration
  const allColumnsValidated = sites.length > 0 && sites.every((_, index) =>
    columnValidationStatus[index]?.status === 'valid'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      toast({
        title: "Validation error",
        description: "Job name is required",
        variant: "destructive",
      });
      return;
    }

    if (sites.length === 0) {
      toast({
        title: "Validation error",
        description: "At least one site configuration is required",
        variant: "destructive",
      });
      return;
    }

    for (const site of sites) {
      if (!site.siteId || !site.driveId) {
        toast({
          title: "Validation error",
          description: "Site ID and Drive ID are required for all sites",
          variant: "destructive",
        });
        return;
      }
    }

    // Check column validation
    if (!allColumnsValidated) {
      toast({
        title: "Column validation required",
        description: "Please validate and provision columns for all sites before saving",
        variant: "destructive",
      });
      return;
    }

    if (scheduleEnabled && !schedule.trim()) {
      toast({
        title: "Validation error",
        description: "Schedule is enabled but no schedule is configured",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);

      const jobData: Partial<SharePointJob> = {
        name,
        description,
        isEnabled,
        dryRun,
        schedule: schedule || undefined,
        scheduleDescription: scheduleDescription || undefined,
        sites,
        modifiedBy: "user", // TODO: Get from auth context
      };

      if (mode === "create") {
        await createSharePointJob(jobData);
        toast({
          title: "Job created",
          description: "SharePoint job has been created successfully",
        });
      } else if (jobId) {
        await updateSharePointJob(jobId, jobData);
        toast({
          title: "Job updated",
          description: "SharePoint job has been updated successfully",
        });
      }

      setLocation("/sharepoint/jobs");
    } catch (error: any) {
      toast({
        title: `Error ${mode === "create" ? "creating" : "updating"} job`,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SharePointLayout>
        <div className="p-6">
          <div className="text-center py-8">Loading job...</div>
        </div>
      </SharePointLayout>
    );
  }

  return (
    <SharePointLayout>
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center gap-4 mb-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/sharepoint/jobs")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">
              {mode === "create" ? "Create" : "Edit"} SharePoint Job
            </h1>
          </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Configure the basic settings for your SharePoint image metadata job
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Job Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production Images Metadata Extraction"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description of what this job does"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="enabled"
                  checked={isEnabled}
                  onCheckedChange={setIsEnabled}
                />
                <Label htmlFor="enabled">Enabled</Label>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="dryRun">Dry run (no SharePoint updates)</Label>
                  <p className="text-sm text-muted-foreground">
                    Scans and analyses files, and logs what would be updated, but does not write any changes back to SharePoint.
                  </p>
                </div>
                <Switch
                  id="dryRun"
                  checked={dryRun}
                  onCheckedChange={setDryRun}
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedule Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Schedule</CardTitle>
              <CardDescription>
                Configure when this job should run automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="sharepoint-schedule-enabled">Run on a schedule</Label>
                  <p className="text-sm text-muted-foreground">
                    If off, the job only runs when manually triggered. Times are interpreted in UTC.
                  </p>
                </div>
                <Switch
                  id="sharepoint-schedule-enabled"
                  checked={scheduleEnabled}
                  onCheckedChange={(next) => {
                    setScheduleEnabled(next);
                    if (!next) {
                      setSchedule("");
                      setScheduleDescription("");
                      setScheduleDescriptionTouched(false);
                    }
                  }}
                />
              </div>

              {scheduleEnabled && (
                <>
                  <div className="space-y-2">
                    <Label>Schedule Editor</Label>
                    <RadioGroup
                      value={scheduleUiMode}
                      onValueChange={(value) => setScheduleUiMode(value as ScheduleUiMode)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="simple" id="sp-schedule-simple" />
                        <Label htmlFor="sp-schedule-simple" className="font-normal cursor-pointer">
                          Simple
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="advanced" id="sp-schedule-advanced" />
                        <Label htmlFor="sp-schedule-advanced" className="font-normal cursor-pointer">
                          Advanced (cron)
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {scheduleUiMode === "simple" && (
                    <div className="space-y-4 rounded-md border p-4">
                      {/* Time picker */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Hour (UTC)</Label>
                          <Select
                            value={timeHour.toString()}
                            onValueChange={(value) => setTimeHour(parseInt(value, 10))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select hour" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                                <SelectItem key={hour} value={hour.toString()}>
                                  {formatTime({ hour, minute: 0 })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Minute</Label>
                          <Select
                            value={timeMinute.toString()}
                            onValueChange={(value) => setTimeMinute(parseInt(value, 10))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select minute" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                                <SelectItem key={minute} value={minute.toString()}>
                                  :{minute.toString().padStart(2, "0")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Repeat interval */}
                      <div className="space-y-2">
                        <Label>Repeat Interval</Label>
                        <Select
                          value={hourlyInterval?.toString() || "none"}
                          onValueChange={(value) =>
                            setHourlyInterval(value === "none" ? undefined : (parseInt(value, 10) as HourlyInterval))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Run once at specified time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Run once at specified time</SelectItem>
                            <SelectItem value="1">Every 1 hour</SelectItem>
                            <SelectItem value="2">Every 2 hours</SelectItem>
                            <SelectItem value="4">Every 4 hours</SelectItem>
                            <SelectItem value="6">Every 6 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Frequency */}
                      <div className="space-y-2">
                        <Label>Frequency</Label>
                        <Select
                          value={frequency}
                          onValueChange={(value) => setFrequency(value as FrequencyType)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekdays">Weekdays (Mon-Fri)</SelectItem>
                            <SelectItem value="weekends">Weekends (Sat-Sun)</SelectItem>
                            <SelectItem value="specific-days">Specific days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {frequency === "specific-days" && (
                        <div className="space-y-2">
                          <Label>Select Days</Label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 1 as DayOfWeek, label: "Mon" },
                              { value: 2 as DayOfWeek, label: "Tue" },
                              { value: 3 as DayOfWeek, label: "Wed" },
                              { value: 4 as DayOfWeek, label: "Thu" },
                              { value: 5 as DayOfWeek, label: "Fri" },
                              { value: 6 as DayOfWeek, label: "Sat" },
                              { value: 0 as DayOfWeek, label: "Sun" },
                            ].map((day) => {
                              const isSelected = specificDays.includes(day.value);
                              return (
                                <Button
                                  key={day.value}
                                  type="button"
                                  variant={isSelected ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    setSpecificDays((current) => {
                                      if (current.includes(day.value)) {
                                        return current.filter((d) => d !== day.value);
                                      }
                                      return [...current, day.value];
                                    });
                                  }}
                                >
                                  {day.label}
                                </Button>
                              );
                            })}
                          </div>
                          {specificDays.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                              Select at least one day.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="text-sm text-muted-foreground">
                        Preview: {formatTimeBasedSchedule({
                          time: { hour: timeHour, minute: timeMinute },
                          frequency,
                          specificDays: frequency === "specific-days" ? specificDays : undefined,
                          hourlyInterval,
                        })}
                      </div>
                    </div>
                  )}

                  {scheduleUiMode === "advanced" && (
                    <div className="space-y-2">
                      <Label htmlFor="schedule">Cron Expression</Label>
                      <Input
                        id="schedule"
                        value={schedule}
                        onChange={(e) => setSchedule(e.target.value)}
                        placeholder="e.g., 0 21 * * 1-5 (weekdays at 9:00 PM)"
                      />
                      <p className="text-sm text-muted-foreground">
                        Format: minutes hours day-of-month month day-of-week (5-part cron). If you paste a 6-part cron,
                        the leading seconds field is ignored.
                      </p>
                    </div>
                  )}

              <div className="space-y-2">
                <Label htmlFor="scheduleDescription">Schedule Description</Label>
                <Input
                  id="scheduleDescription"
                  value={scheduleDescription}
                  onChange={(e) => {
                    setScheduleDescriptionTouched(true);
                    setScheduleDescription(e.target.value);
                  }}
                  placeholder="e.g., Every 6 hours"
                />
              </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Site Configurations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>SharePoint Sites</CardTitle>
                  <CardDescription>
                    Configure one or more SharePoint sites to process
                  </CardDescription>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addSite}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Site
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {sites.map((site, index) => (
                  <AccordionItem key={index} value={`site-${index}`}>
                    <AccordionTrigger>
                      Site {index + 1}
                      {site.siteId && ` - ${site.siteId.substring(0, 30)}...`}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-4">
                        {/* Basic Site Info */}
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h4 className="font-medium">Basic Site Info</h4>
                            <p className="text-sm text-muted-foreground">
                              Use the picker to avoid manually looking up IDs.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openSitePicker(index)}
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Pick From SharePoint
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Site ID *</Label>
                            <Input
                              value={site.siteId}
                              onChange={(e) =>
                                updateSite(index, { siteId: e.target.value })
                              }
                              placeholder="your-tenant.sharepoint.com,site-guid,web-guid"
                              required
                            />
                            <p className="text-xs text-muted-foreground">
                              Microsoft Graph siteId. The picker fills this automatically.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Drive ID *</Label>
                            <Input
                              value={site.driveId}
                              onChange={(e) =>
                                updateSite(index, { driveId: e.target.value })
                              }
                              placeholder="b!drive-id"
                              required
                            />
                            <p className="text-xs text-muted-foreground">
                              Microsoft Graph driveId for the document library (e.g. “Documents” / “Shared Documents”).
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Folder Path</Label>
                            <Input
                              value={site.folderPath || ""}
                              onChange={(e) =>
                                updateSite(index, { folderPath: e.target.value })
                              }
                              placeholder="Optional starting folder path"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Max Items Per Run</Label>
                            <Input
                              type="number"
                              value={site.maxItemsPerRun || 100}
                              onChange={(e) =>
                                updateSite(index, {
                                  maxItemsPerRun: parseInt(e.target.value),
                                })
                              }
                            />
                          </div>
                        </div>

                        {/* Column Mappings */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">Column Mappings</h4>
                            {/* Column validation status indicator */}
                            {columnValidationStatus[index]?.status === 'valid' && (
                              <span className="flex items-center gap-1 text-sm text-green-600">
                                <CheckCircle2 className="h-4 w-4" />
                                Validated
                              </span>
                            )}
                            {columnValidationStatus[index]?.status === 'error' && (
                              <span className="flex items-center gap-1 text-sm text-red-600">
                                <XCircle className="h-4 w-4" />
                                Failed
                              </span>
                            )}
                            {!columnValidationStatus[index] && (
                              <span className="flex items-center gap-1 text-sm text-amber-600">
                                <AlertCircle className="h-4 w-4" />
                                Not validated
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            These columns will be created on the SharePoint document library if they don't exist.
                            You must validate columns before saving.
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(site.columns).map(([key, value]) => (
                              <div key={key} className="space-y-1">
                                <Label className="text-xs">
                                  {key.replace(/([A-Z])/g, " $1").trim()}
                                </Label>
                                <Input
                                  value={value}
                                  onChange={(e) =>
                                    updateSiteColumns(index, key, e.target.value)
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                            ))}
                          </div>

                          {/* Validation result details */}
                          {columnValidationStatus[index]?.result && (
                            <div className="mt-3 text-xs space-y-1">
                              {columnValidationStatus[index].result!.createdColumns.length > 0 && (
                                <p className="text-green-600">
                                  Created: {columnValidationStatus[index].result!.createdColumns.map(c => c.name).join(', ')}
                                </p>
                              )}
                              {columnValidationStatus[index].result!.failedColumns.length > 0 && (
                                <p className="text-red-600">
                                  Failed: {columnValidationStatus[index].result!.failedColumns.map(c => `${c.name} (${c.error})`).join(', ')}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Validate button */}
                          <Button
                            type="button"
                            variant={columnValidationStatus[index]?.status === 'valid' ? 'outline' : 'default'}
                            size="sm"
                            onClick={() => handleValidateColumns(index)}
                            disabled={!site.siteId || !site.driveId || columnValidationStatus[index]?.status === 'validating'}
                            className="mt-2"
                          >
                            {columnValidationStatus[index]?.status === 'validating' ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Validating...
                              </>
                            ) : columnValidationStatus[index]?.status === 'valid' ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Re-validate Columns
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Validate & Provision Columns
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Folder Scan Configuration */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <h4 className="font-medium">Folder Scanning</h4>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={site.folderScanConfig?.recursive ?? true}
                              onCheckedChange={(checked) =>
                                updateFolderScanConfig(index, "recursive", checked)
                              }
                            />
                            <Label>Recursive scanning</Label>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Max Depth</Label>
                            <Input
                              type="number"
                              value={site.folderScanConfig?.maxDepth || 10}
                              onChange={(e) =>
                                updateFolderScanConfig(
                                  index,
                                  "maxDepth",
                                  parseInt(e.target.value)
                                )
                              }
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Include Patterns</Label>
                            <Textarea
                              value={
                                site.folderScanConfig?.includePatterns?.join("\n") || ""
                              }
                              onChange={(e) =>
                                updateFolderScanConfig(
                                  index,
                                  "includePatterns",
                                  e.target.value.split("\n").filter((p) => p.trim())
                                )
                              }
                              placeholder="**/2 Production/**"
                              rows={3}
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              One pattern per line
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Exclude Patterns</Label>
                            <Textarea
                              value={
                                site.folderScanConfig?.excludePatterns?.join("\n") || ""
                              }
                              onChange={(e) =>
                                updateFolderScanConfig(
                                  index,
                                  "excludePatterns",
                                  e.target.value.split("\n").filter((p) => p.trim())
                                )
                              }
                              placeholder="**/Archives/**"
                              rows={3}
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              One pattern per line
                            </p>
                          </div>
                        </div>

                        {/* Quality Configuration */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <h4 className="font-medium">Quality Validation</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-sm">Min DPI</Label>
                              <Input
                                type="number"
                                value={site.qualityConfig?.minDPI || 220}
                                onChange={(e) =>
                                  updateQualityConfig(
                                    index,
                                    "minDPI",
                                    parseInt(e.target.value)
                                  )
                                }
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-sm">Preferred DPI</Label>
                              <Input
                                type="number"
                                value={site.qualityConfig?.preferredDPI || 300}
                                onChange={(e) =>
                                  updateQualityConfig(
                                    index,
                                    "preferredDPI",
                                    parseInt(e.target.value)
                                  )
                                }
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-sm">Expected Color Mode</Label>
                              <Select
                                value={site.qualityConfig?.expectedColorMode || "RGB"}
                                onValueChange={(value) =>
                                  updateQualityConfig(
                                    index,
                                    "expectedColorMode",
                                    value as "RGB" | "Grayscale" | "CMYK"
                                  )
                                }
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="RGB">RGB</SelectItem>
                                  <SelectItem value="Grayscale">Grayscale</SelectItem>
                                  <SelectItem value="CMYK">CMYK</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Email Notifications */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <h4 className="font-medium">📧 Email Notifications</h4>
                          <p className="text-xs text-muted-foreground">
                            Send email alerts when quality thresholds are breached or jobs fail.
                            Requires Mail.Send permission in Azure AD.
                          </p>
                          
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={site.notifications?.enabled ?? false}
                              onCheckedChange={(checked) =>
                                updateNotificationConfig(index, "enabled", checked)
                              }
                            />
                            <Label>Enable email notifications</Label>
                          </div>

                          {site.notifications?.enabled && (
                            <div className="space-y-4 pt-2">
                              <div className="space-y-2">
                                <Label className="text-sm">From Address *</Label>
                                <Input
                                  type="email"
                                  value={site.notifications?.fromAddress || ""}
                                  onChange={(e) =>
                                    updateNotificationConfig(index, "fromAddress", e.target.value)
                                  }
                                  placeholder="sharepoint-alerts@yourdomain.com"
                                  className="h-8"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Must be a valid mailbox in your tenant
                                </p>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm">Recipients *</Label>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addRecipient(index)}
                                    className="h-6 text-xs"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                </div>
                                {(site.notifications?.recipients || []).map((recipient, rIdx) => (
                                  <div key={rIdx} className="flex gap-2 items-center">
                                    <Input
                                      type="email"
                                      value={recipient.email}
                                      onChange={(e) =>
                                        updateRecipient(index, rIdx, "email", e.target.value)
                                      }
                                      placeholder="user@company.com"
                                      className="h-8 flex-1"
                                    />
                                    <Input
                                      value={recipient.name || ""}
                                      onChange={(e) =>
                                        updateRecipient(index, rIdx, "name", e.target.value)
                                      }
                                      placeholder="Name (optional)"
                                      className="h-8 w-32"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeRecipient(index, rIdx)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </div>
                                ))}
                                {(site.notifications?.recipients?.length || 0) === 0 && (
                                  <p className="text-xs text-muted-foreground italic">
                                    No recipients added. Click "Add" to add a recipient.
                                  </p>
                                )}
                              </div>

                              <div className="border-t pt-3 space-y-3">
                                <Label className="text-sm font-medium">Alert Thresholds</Label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Min Pass Rate (%)</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={site.notifications?.thresholds?.minPassRate ?? ""}
                                      onChange={(e) =>
                                        updateNotificationThreshold(
                                          index,
                                          "minPassRate",
                                          e.target.value ? parseInt(e.target.value) : undefined
                                        )
                                      }
                                      placeholder="e.g., 80"
                                      className="h-8"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Alert when pass rate drops below
                                    </p>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Max Fail Count</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={site.notifications?.thresholds?.maxFailCount ?? ""}
                                      onChange={(e) =>
                                        updateNotificationThreshold(
                                          index,
                                          "maxFailCount",
                                          e.target.value ? parseInt(e.target.value) : undefined
                                        )
                                      }
                                      placeholder="e.g., 10"
                                      className="h-8"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Alert when fail count exceeds
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={site.notifications?.thresholds?.alwaysSendSummary ?? false}
                                      onCheckedChange={(checked) =>
                                        updateNotificationThreshold(index, "alwaysSendSummary", checked)
                                      }
                                    />
                                    <Label className="text-sm">Always send summary after job completion</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={site.notifications?.thresholds?.notifyOnJobFailure ?? false}
                                      onCheckedChange={(checked) =>
                                        updateNotificationThreshold(index, "notifyOnJobFailure", checked)
                                      }
                                    />
                                    <Label className="text-sm">Send alert on job processing errors</Label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {sites.length > 1 && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeSite(index)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove Site
                          </Button>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <SitePickerDialog
            open={sitePickerOpen}
            onOpenChange={setSitePickerOpen}
            initialUrl={sitePickerInitialUrl}
            onApply={(result) => {
              if (sitePickerIndex === null) return;
              updateSite(sitePickerIndex, {
                siteId: result.siteId,
                driveId: result.driveId,
              });
              toast({
                title: "SharePoint site selected",
                description: `${result.siteName || result.siteId} · ${result.driveName || result.driveId}`,
              });
            }}
          />

          {/* Actions */}
          <div className="flex flex-col items-end gap-2">
            {!allColumnsValidated && sites.length > 0 && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                Please validate columns for all sites before saving
              </p>
            )}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLocation("/sharepoint/jobs")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !allColumnsValidated}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : mode === "create" ? "Create Job" : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
        </form>
      </div>
    </SharePointLayout>
  );
}
